import React, { useState, useEffect } from 'react';

const FloorPlanPopup = ({ isOpen, onClose, planData }) => {
  const [isLoading, setIsLoading] = useState(true);

  // Simple loading spinner component
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-600"></div>
    </div>
  );

  // Handle image load complete
  const handleImageLoaded = () => {
    setIsLoading(false);
  };

  // Reset loading state when modal opens/closes
  useEffect(() => {
    if (isOpen && planData) {
      setIsLoading(true);
      document.body.style.overflow = 'hidden';
    } else {
      setIsLoading(false);
      document.body.style.overflow = 'auto';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, planData]);

  const handleClose = () => {
    document.body.style.overflow = 'auto';
    onClose();
  };

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen || !planData) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-4 relative">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Floor Plan - {planData.type} ({planData.sqft} sqft)
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Image container */}
          <div className="relative">
            {isLoading && <LoadingSpinner />}
            <img
              src={planData.image}
              alt={`Floor plan ${planData.type}`}
              className={`w-full h-auto max-h-[60vh] object-contain ${isLoading ? 'hidden' : 'block'}`}
              onLoad={handleImageLoaded}
              onError={() => setIsLoading(false)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Type:</span> {planData.type} | 
            <span className="font-medium ml-2">Area:</span> {planData.sqft} sqft
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium py-2 px-4 rounded transition-colors duration-200"
            >
              Close
            </button>
            <a
              href={planData.image}
              download={`Floor_Plan_${planData.type}.jpg`}
              className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium py-2 px-4 rounded flex items-center transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanPopup;